import React, { FC, useRef } from 'react'
import arrowTriangle from '@assets/images/arrow-triangle.svg'
import { Restore } from '@mui/icons-material'
import { Box, CircularProgress, IconButton } from '@mui/material'
import { IMessage } from '@stomp/stompjs'

import { CustomSvgIcon } from '@components/CustomSvgIcon'
import { CursorPosition } from '@components/NestedMenu'

import { buttonStylesByMode } from '@helpers'
import { DEFAULT_TIME_IN_SECONDS_FOR_UPDATE_INTERVAL, UPDATE_TYPE } from '@constants'
import { EntityType } from '@types'

import { UpdateSettingsPopup } from './components/UpdateSettingsPopup/UpdateSettingsPopup'
import { useUpdateSettings } from './hooks/useUpdateSettings'

type PropsType = {
  position: CursorPosition | null
  isUpdateLoading: boolean
  onRefreshAllPages: () => void
  onObjectSubscribe: (msg: IMessage) => Promise<void>
  entity: EntityType
  isActiveTimeWindow?: boolean
}

export const UpdateSettings: FC<PropsType> = ({
  position,
  isUpdateLoading,
  onRefreshAllPages,
  onObjectSubscribe,
  entity,
  isActiveTimeWindow,
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null)

  const { state, handlers } = useUpdateSettings({
    onObjectSubscribe,
    onRefreshAllPages,
    entity,
  })

  const { isUpdateSettingsOpen, updateType } = state

  const { handleToggleUpdateSettings, handleSwitchUpdateType } = handlers

  return (
    <>
      <Box
        ref={anchorRef}
        className={'menuButton'}
        sx={theme => ({
          ...buttonStylesByMode(theme, true),
          py: 0,
          px: 0,
          margin: '4px',
          display: 'flex',
          mr: 1.5,
          minWidth: 0,
          ...(position && {
            background: theme.palette.background.buttonHovered,
            boxShadow: theme.palette.boxShadow.buttonHovered,
          }),
        })}
      >
        {isUpdateLoading ? (
          <Box
            sx={{
              width: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={14} />
          </Box>
        ) : (
          <IconButton sx={{ width: 24 }} onClick={() => onRefreshAllPages()}>
            <Restore sx={{ fontSize: '1.2rem' }} />
          </IconButton>
        )}
        <IconButton sx={{ width: 24 }} onClick={() => handleToggleUpdateSettings(true)}>
          <CustomSvgIcon
            className={'menuButton'}
            imgClassName={'menuButton'}
            src={arrowTriangle}
            sx={{ fontSize: '1.1rem' }}
          />
        </IconButton>
      </Box>
      <UpdateSettingsPopup
        anchorRef={anchorRef}
        isActiveTimeWindow={isActiveTimeWindow}
        isUpdateSettingsOpen={isUpdateSettingsOpen}
        updateType={updateType || UPDATE_TYPE.WEBSOCKET}
        updateInterval={
          entity.updateSetting?.interval || DEFAULT_TIME_IN_SECONDS_FOR_UPDATE_INTERVAL
        }
        onSwitchUpdateType={handleSwitchUpdateType}
        onToggleUpdateSettings={handleToggleUpdateSettings}
      />
    </>
  )
}
